import {
    type IListPageChildProps,
    type IListPageLayoutProps,
    ListPageLayout,
} from "layouts/ListPageLayout";
import { RESEARCH_PREVIEW_SHELL_NAV_ITEMS } from "layouts/ResearchPreviewShellNavItems";
import { type ReactNode, useContext, useState } from "react";
import { Route, type RouteComponentProps } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
    IconJobs,
    IconMagnifyingGlass,
    IconPlus,
    IconTray,
    IconUser,
} from "@btnet/blueprint-icons";
import { BTHorizontalNav } from "@btnet/core-components/BTHorizontalNav";
import { BTShellLayout, type IShellNavItem } from "@btnet/core-components/BTShellLayout";
import { BTVerticalNav } from "@btnet/core-components/BTVerticalNav";
import { MD_BREAKPOINT } from "@btnet/core-components/types/breakpoints";

import { AppProvider } from "helpers/AppProvider";
import { AppProviderHandler } from "helpers/AppProvider.api.handler";
import { BTLinkSpaRoutingProvider } from "helpers/globalContext/BTLinkSpaRoutingProvider";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { DefaultEnabledSpaInfo, SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { getJobPickerState, getSelectedJobId } from "utilities/jobPicker/jobPicker";

import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { InfoArchOptInToggle } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/InfoArchOptInToggle";
import { useInfoArchOptIn } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/useInfoArchOptIn";

/** Matches ListPageLayout's module-level default so a route gets the same handler instance. */
const defaultAppProviderHandler = new AppProviderHandler();

/**
 * The per-page provider stack `ListPageLayout` adds around page content (see
 * `ListPageLayout.tsx`, `ListPageLayout`/`ListPageLayoutContext`). Wraps `BTShellLayout`'s
 * `children` canvas slot so the ported page picks up the same context it would under
 * `ListPageLayout`.
 */
const BTShellWrapper = ({
    children,
    appProviderHandler = defaultAppProviderHandler,
}: {
    children: ReactNode;
    appProviderHandler?: AppProviderHandler;
}) => (
    <AppProvider handler={appProviderHandler}>
        <FocusProvider shareFocusWithParent>
            <SpaInfoContext.Provider value={DefaultEnabledSpaInfo}>
                <BTLinkSpaRoutingProvider>{children}</BTLinkSpaRoutingProvider>
            </SpaInfoContext.Provider>
        </FocusProvider>
    </AppProvider>
);

interface IShellCanvasProps extends IListPageLayoutProps {
    matchProps: RouteComponentProps;
}

/**
 * Canvas slot: invokes the v1 page function exactly as `ListPageLayout` does, so a ported
 * page renders unchanged.
 *
 * Job selection always happens in the v1 job picker — a user picks a job outside
 * research-preview — so the shell has no picker UI and no picker load. It reads the user's
 * persisted selection via `getJobPickerState()` (which holds the real selected job ids, or the
 * full set for All-Jobs) and synthesizes `IListPageChildProps` synchronously.
 *
 * Simplifications vs. v1 (research-preview scope): the selected job's display name is not
 * resolved (a single job's `jobName` is empty; All-Jobs uses the route label), and a builder's
 * General job is not surfaced. If the user has no selection the page still renders and shows
 * its own "select a jobsite" empty state.
 */
const ShellCanvas = ({ matchProps, component, allJobsText }: IShellCanvasProps) => {
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    const builderId = builderInfo?.builderId ?? 0;

    // The v1 picker persists real job ids (single selection, or every selected job for
    // All-Jobs); filter out the AllJobs(0)/NoJobs(-1) sentinels.
    const { selectedJobIds, isAllJobsSelected, isTemplateMode } = getJobPickerState();
    let jobIds = selectedJobIds.filter((id) => id > 0);
    if (jobIds.length === 0) {
        // No picker selection — fall back to the remembered single jobsite selection.
        const selectedJobId = getSelectedJobId(builderId);
        if (selectedJobId > 0) {
            jobIds = [selectedJobId];
        }
    }

    const listLayoutProps: IListPageChildProps = {
        key: 0,
        builderId,
        jobIds,
        jobName: isAllJobsSelected ? (allJobsText ?? "All Jobs") : "",
        userId: userInfo?.globalUserId ?? 0,
        isTemplateMode,
        loginType: userInfo?.loginType ?? BTLoginTypes.BUILDER,
        // Selection is read synchronously from storage, so the page is ready immediately. An
        // empty selection still renders the page, which shows its "select a jobsite" state.
        jobPickerReady: true,
        isAllJobsSelected,
        jobPickerCount: jobIds.length,
        setJobPickerCollapsed: () => undefined,
    };

    return <>{component(listLayoutProps, matchProps)}</>;
};

/** Sidebar slot — real nav data wiring is a follow-up; the research-preview toggle is live. */
const ShellSidebar = ({
    navItems,
    selectedKey,
    onSelectKey,
}: {
    navItems: IShellNavItem[];
    selectedKey: string;
    onSelectKey: (key: string) => void;
}) => (
    <BTVerticalNav
        data-testid="research-preview-sidebar"
        navItems={navItems}
        selectedKey={selectedKey}
        onSelectKey={onSelectKey}
        top={<>TBD</>}
        bottom={
            <InfoArchOptInToggle
                label="Research preview"
                labelClassName="text-fg-secondary opacity-100"
                testIdPrefix="researchPreviewSidebarOptIn"
            />
        }
    />
);

/** Matches `BTHorizontalNav.stories.tsx`'s dummy data — real bottombar items are a follow-up. */
const DUMMY_BOTTOMBAR_NAV_ITEMS = [
    { key: "inbox", label: "Inbox", icon: <IconTray /> },
    { key: "jobs", label: "Jobs", icon: <IconJobs /> },
    { key: "add", label: "Add", icon: <IconPlus /> },
    { key: "search", label: "Search", icon: <IconMagnifyingGlass /> },
    { key: "account", label: "Account", icon: <IconUser /> },
];

const ShellBottombar = () => {
    // Intentionally separate selection from BTShellLayout's shared state, matching the v1 story
    // where the bottombar previews its own nav data.
    const [selectedKey, setSelectedKey] = useState(DUMMY_BOTTOMBAR_NAV_ITEMS[0].key);
    return (
        <BTHorizontalNav
            data-testid="research-preview-bottombar"
            navItems={DUMMY_BOTTOMBAR_NAV_ITEMS}
            selectedKey={selectedKey}
            onSelectKey={setSelectedKey}
        />
    );
};

/**
 * Research-preview (info-arch) per-route layout (ado-286232).
 *
 * Porting a v1 list page to the BTShellLayout is a one-line route change: swap
 * `<ListPageLayout .../>` for `<ResearchPreviewLayout .../>` with identical props. Opted-in
 * users (`userInfo.optIns.infoArch === true`) get the page rendered inside `BTShellLayout`'s canvas
 * slot; everyone else falls through to `ListPageLayout` unchanged, so each swap is independent
 * and safe. Routes not yet ported keep their `ListPageLayout` registration.
 */
export const ResearchPreviewLayout = (props: IListPageLayoutProps) => {
    const { isOptedIn } = useInfoArchOptIn();
    const mdBreakpoint = useMediaQuery(`(max-width: ${MD_BREAKPOINT}px)`);
    const [selectedNavKey, setSelectedNavKey] = useState(RESEARCH_PREVIEW_SHELL_NAV_ITEMS[0].key);
    if (!isOptedIn) {
        return <ListPageLayout {...props} />;
    }

    // ListPageLayout registers the route internally; in shell mode we register it here so the
    // canvas can hand `matchProps` to the v1 page function (ShellCanvas). `component` is
    // excluded from the spread because it collides with Route's own `component` prop, which
    // would silently win over `render` — ShellCanvas reads it off `props` directly instead.
    const { component: _component, ...routeProps } = props;
    return (
        <Route
            {...routeProps}
            render={(matchProps) => (
                <BTShellWrapper appProviderHandler={props.appProviderHandler}>
                    <BTShellLayout
                        data-testid="research-preview-shell"
                        sidebar={
                            !mdBreakpoint ? (
                                <ShellSidebar
                                    navItems={RESEARCH_PREVIEW_SHELL_NAV_ITEMS}
                                    selectedKey={selectedNavKey}
                                    onSelectKey={setSelectedNavKey}
                                />
                            ) : undefined
                        }
                        bottombar={mdBreakpoint ? <ShellBottombar /> : undefined}
                    >
                        <ShellCanvas {...props} matchProps={matchProps} />
                    </BTShellLayout>
                </BTShellWrapper>
            )}
        />
    );
};
